import { useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Buildings,
  CurrencyEur,
  Plus,
  Trash,
  Info,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASES = [
  { code: 'ESQ', label: 'Esquisse' },
  { code: 'APS', label: 'Avant-Projet Sommaire' },
  { code: 'APD', label: 'Avant-Projet Définitif' },
  { code: 'PC', label: 'Permis de Construire' },
  { code: 'PRO', label: 'Projet' },
  { code: 'DCE', label: 'Dossier de Consultation' },
  { code: 'ACT', label: 'Assistance Contrats Travaux' },
  { code: 'VISA', label: 'Visa' },
  { code: 'DET', label: 'Direction Exécution Travaux' },
  { code: 'AOR', label: 'Assistance Opérations Réception' },
];

const STEP_LABELS = ['Chantier', 'Lots & Entreprises', 'Factures', 'Récapitulatif'];

export default function OnboardingWizard({ open, onClose, chantierId, chantier, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1 — Project info
  const [projectForm, setProjectForm] = useState({
    current_phase: chantier?.current_phase || '',
    start_date: chantier?.start_date?.slice(0, 10) || '',
    end_date: chantier?.end_date?.slice(0, 10) || '',
    status: chantier?.status || 'en_cours',
  });

  // Step 2 — Lots
  const [lots, setLots] = useState([]);
  const [lotForm, setLotForm] = useState({ code: '', nom: '', corps_etat: '', entreprise_nom: '', entreprise_email: '', montant_marche: '', statut: 'attribue' });

  // Step 3 — Factures
  const [factures, setFactures] = useState([]);
  const [factForm, setFactForm] = useState({ numero: '', entreprise: '', lot: '', montant_ht: '', statut: 'payee' });

  const addLot = () => {
    if (!lotForm.code || !lotForm.nom) return toast.error('Code et nom requis');
    setLots([...lots, { ...lotForm, montant_marche: lotForm.montant_marche ? parseFloat(lotForm.montant_marche) : null }]);
    setLotForm({ code: '', nom: '', corps_etat: '', entreprise_nom: '', entreprise_email: '', montant_marche: '', statut: 'attribue' });
  };

  const addFacture = () => {
    if (!factForm.numero || !factForm.montant_ht) return toast.error('Numéro et montant requis');
    setFactures([...factures, { ...factForm, montant_ht: parseFloat(factForm.montant_ht) }]);
    setFactForm({ numero: '', entreprise: '', lot: '', montant_ht: '', statut: 'payee' });
  };

  const handleImport = async () => {
    setSaving(true);
    try {
      // Step 1: Update project
      if (projectForm.current_phase || projectForm.start_date) {
        await axios.put(`${API_URL}/api/chantiers/${chantierId}`, {
          current_phase: projectForm.current_phase || undefined,
          start_date: projectForm.start_date || undefined,
          end_date: projectForm.end_date || undefined,
          status: projectForm.status,
        }, { withCredentials: true });
      }

      // Step 2: Create lots
      for (const lot of lots) {
        try {
          await axios.post(`${API_URL}/api/chantiers/${chantierId}/marches`, lot, { withCredentials: true });
        } catch (e) {
          console.warn(`Lot ${lot.code}: ${e.response?.data?.detail || e.message}`);
        }
      }

      // Step 3: Create factures
      for (const f of factures) {
        try {
          await axios.post(`${API_URL}/api/chantiers/${chantierId}/factures`, {
            ...f,
            tva: 20,
          }, { withCredentials: true });
        } catch (e) {
          console.warn(`Facture ${f.numero}: ${e.response?.data?.detail || e.message}`);
        }
      }

      toast.success(`Import terminé : ${lots.length} lot(s), ${factures.length} facture(s)`);
      onComplete?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setSaving(false);
    }
  };

  const totalMarches = lots.reduce((s, l) => s + (l.montant_marche || 0), 0);
  const totalFactures = factures.reduce((s, f) => s + (f.montant_ht || 0), 0);
  const fmt = (n) => n ? n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) : '0 €';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-wizard">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Importer un chantier en cours
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-6">
          {STEP_LABELS.map((label, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? 'text-[#2C2C2C]' : 'text-[#9A9A9A]'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  i < step ? 'bg-[#2D6A4F] border-[#2D6A4F] text-white' :
                  i === step ? 'border-[#C4773B] text-[#C4773B]' : 'border-[#E0E0DB] text-[#9A9A9A]'
                }`}>
                  {i < step ? <Check size={14} weight="bold" /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block">{label}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-[#2D6A4F]' : 'bg-[#E0E0DB]'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Project */}
        {step === 0 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div className="p-3 bg-[#EFF6FF] border border-[#1E3A5F]/20 text-sm text-[#1E3A5F] flex items-start gap-2">
              <Info size={16} className="flex-shrink-0 mt-0.5" />
              <span>Renseignez la phase actuelle du chantier. Les phases précédentes seront automatiquement marquées comme terminées.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Phase actuelle</Label>
                <Select value={projectForm.current_phase || 'none'} onValueChange={v => setProjectForm({ ...projectForm, current_phase: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="wizard-phase-select"><SelectValue placeholder="Phase" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucune</SelectItem>
                    {PHASES.map(p => <SelectItem key={p.code} value={p.code}>{p.code} — {p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select value={projectForm.status} onValueChange={v => setProjectForm({ ...projectForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospection">Prospection</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="en_pause">En pause</SelectItem>
                    <SelectItem value="termine">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date de démarrage</Label>
                <Input type="date" value={projectForm.start_date} onChange={e => setProjectForm({ ...projectForm, start_date: e.target.value })} className="rounded-none mt-1" data-testid="wizard-start-date" />
              </div>
              <div>
                <Label>Date fin prévisionnelle</Label>
                <Input type="date" value={projectForm.end_date} onChange={e => setProjectForm({ ...projectForm, end_date: e.target.value })} className="rounded-none mt-1" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Lots */}
        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            {lots.length > 0 && (
              <div className="border border-[#E0E0DB] divide-y divide-[#E0E0DB]">
                {lots.map((lot, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <span className="font-semibold">{lot.code}</span> — {lot.nom}
                      {lot.entreprise_nom && <span className="text-[#6B6B6B] ml-2">({lot.entreprise_nom})</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{lot.montant_marche ? fmt(lot.montant_marche) : '—'}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#C53030]" onClick={() => setLots(lots.filter((_, j) => j !== i))}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="px-3 py-2 bg-[#FAFAF8] text-sm font-semibold text-right">
                  Total : {fmt(totalMarches)}
                </div>
              </div>
            )}
            <div className="grid grid-cols-6 gap-2 items-end">
              <div>
                <Label className="text-xs">Code *</Label>
                <Input value={lotForm.code} onChange={e => setLotForm({ ...lotForm, code: e.target.value })} placeholder="LOT01" className="rounded-none mt-1 text-sm" data-testid="wizard-lot-code" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Nom *</Label>
                <Input value={lotForm.nom} onChange={e => setLotForm({ ...lotForm, nom: e.target.value })} placeholder="Gros Oeuvre" className="rounded-none mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Entreprise</Label>
                <Input value={lotForm.entreprise_nom} onChange={e => setLotForm({ ...lotForm, entreprise_nom: e.target.value })} placeholder="Bâti Pro" className="rounded-none mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Montant HT</Label>
                <Input type="number" value={lotForm.montant_marche} onChange={e => setLotForm({ ...lotForm, montant_marche: e.target.value })} placeholder="85000" className="rounded-none mt-1 text-sm" />
              </div>
              <div>
                <Button type="button" onClick={addLot} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full gap-1" data-testid="wizard-add-lot">
                  <Plus size={14} /> Ajouter
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Factures */}
        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <p className="text-sm text-[#6B6B6B]">Optionnel — Saisissez les factures déjà reçues ou payées.</p>
            {factures.length > 0 && (
              <div className="border border-[#E0E0DB] divide-y divide-[#E0E0DB]">
                {factures.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <span className="font-semibold">{f.numero}</span> — {f.entreprise}
                      {f.lot && <span className="text-[#6B6B6B] ml-1">({f.lot})</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{fmt(f.montant_ht)}</span>
                      <span className={`text-xs px-1.5 py-0.5 ${f.statut === 'payee' ? 'bg-[#F0FDF4] text-[#2D6A4F]' : 'bg-[#FFFBEB] text-[#D4880F]'}`}>
                        {f.statut === 'payee' ? 'Payée' : 'En attente'}
                      </span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#C53030]" onClick={() => setFactures(factures.filter((_, j) => j !== i))}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="px-3 py-2 bg-[#FAFAF8] text-sm font-semibold text-right">
                  Total HT : {fmt(totalFactures)}
                </div>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2 items-end">
              <div>
                <Label className="text-xs">N° Facture *</Label>
                <Input value={factForm.numero} onChange={e => setFactForm({ ...factForm, numero: e.target.value })} placeholder="FA-001" className="rounded-none mt-1 text-sm" data-testid="wizard-fact-numero" />
              </div>
              <div>
                <Label className="text-xs">Entreprise</Label>
                <Input value={factForm.entreprise} onChange={e => setFactForm({ ...factForm, entreprise: e.target.value })} placeholder="Bâti Pro" className="rounded-none mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Montant HT *</Label>
                <Input type="number" value={factForm.montant_ht} onChange={e => setFactForm({ ...factForm, montant_ht: e.target.value })} placeholder="12000" className="rounded-none mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={factForm.statut} onValueChange={v => setFactForm({ ...factForm, statut: v })}>
                  <SelectTrigger className="rounded-none mt-1 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payee">Payée</SelectItem>
                    <SelectItem value="en_attente">En attente</SelectItem>
                    <SelectItem value="validee">Validée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Button type="button" onClick={addFacture} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none w-full gap-1" data-testid="wizard-add-facture">
                  <Plus size={14} /> Ajouter
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Recap */}
        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border border-[#E0E0DB] text-center">
                <p className="text-2xl font-bold text-[#2C2C2C]">{projectForm.current_phase || '—'}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Phase actuelle</p>
              </div>
              <div className="p-4 border border-[#E0E0DB] text-center">
                <p className="text-2xl font-bold text-[#2C2C2C]">{lots.length}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Lots — {fmt(totalMarches)}</p>
              </div>
              <div className="p-4 border border-[#E0E0DB] text-center">
                <p className="text-2xl font-bold text-[#2C2C2C]">{factures.length}</p>
                <p className="text-xs text-[#6B6B6B] mt-1">Factures — {fmt(totalFactures)}</p>
              </div>
            </div>

            {lots.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-2">Lots</p>
                <div className="text-sm space-y-1">
                  {lots.map((l, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{l.code} — {l.nom} {l.entreprise_nom && `(${l.entreprise_nom})`}</span>
                      <span className="font-medium">{l.montant_marche ? fmt(l.montant_marche) : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {factures.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#6B6B6B] uppercase tracking-wider mb-2">Factures</p>
                <div className="text-sm space-y-1">
                  {factures.map((f, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{f.numero} — {f.entreprise}</span>
                      <span className="font-medium">{fmt(f.montant_ht)} ({f.statut})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-[#E0E0DB]">
          <Button type="button" variant="outline" className="rounded-none gap-1" onClick={() => step === 0 ? onClose() : setStep(step - 1)}>
            <ArrowLeft size={14} /> {step === 0 ? 'Annuler' : 'Précédent'}
          </Button>
          <div className="flex gap-2">
            {step < 3 && step > 0 && (
              <Button type="button" variant="ghost" className="rounded-none text-[#6B6B6B]" onClick={() => setStep(step + 1)}>
                Passer
              </Button>
            )}
            {step < 3 ? (
              <Button type="button" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-1" onClick={() => setStep(step + 1)} data-testid="wizard-next">
                Suivant <ArrowRight size={14} />
              </Button>
            ) : (
              <Button type="button" className="bg-[#2D6A4F] hover:bg-[#1E5A3F] rounded-none gap-1" onClick={handleImport} disabled={saving} data-testid="wizard-validate">
                <Check size={14} weight="bold" /> {saving ? 'Import en cours...' : 'Valider l\'import'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
